// /login — email + password for the admin console (FACTS PACK: e2e-admin-console-stable@e2e-test.kinio.ai).
// On success the backend sets the httpOnly refresh cookie; the access token stays
// in memory (AuthContext). Role gating happens AFTER auth in RequireAdmin — a
// non-admin who logs in here lands on the 無權限 screen, not the console.

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { config } from '../../config';
import { BrandLogo } from '../../components/BrandLogo';
import { PasswordInput } from '../../components/PasswordInput';
import { isEmailAllowedForPlatformDomain, platformEmailDomain, requestPasswordReset } from './passwordReset';
import type { PasswordResetChannel } from './passwordReset';
import '../_shared/shared.css';
import './auth.css';

type LoginView = 'login' | 'forgot' | 'forgot-sent';

export default function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetChannel, setResetChannel] = useState<PasswordResetChannel>('email');
  const [view, setView] = useState<LoginView>('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allowedDomain = platformEmailDomain();

  if (status === 'authenticated') return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`登入失敗：${err.message}`);
      } else {
        setError('登入失敗：網路連線異常，請稍後再試。');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onForgotSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!allowedDomain) {
      setError('平台管理信箱網域尚未設定，請聯絡維運。');
      return;
    }
    if (!isEmailAllowedForPlatformDomain(resetEmail, allowedDomain)) {
      setError(`請輸入 @${allowedDomain} 的平台管理帳號。`);
      return;
    }
    setBusy(true);
    try {
      await requestPasswordReset(resetEmail, resetChannel);
      setView('forgot-sent');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`無法寄送重設信：${err.message}`);
      } else {
        setError('無法寄送重設信：網路連線異常，請稍後再試。');
      }
    } finally {
      setBusy(false);
    }
  }

  function showForgot() {
    setResetEmail(email);
    setError(null);
    setView('forgot');
  }

  function showLogin() {
    setError(null);
    setView('login');
  }

  return (
    <div className="auth-shell">
      <section className="card auth-card">
        <div className="auth-brand">
          <BrandLogo />
          <div>
            <h1 className="auth-title">管理主控台</h1>
            <p className="text-tertiary auth-subtitle">LS Cloud Admin Console</p>
          </div>
        </div>
        {view === 'login' && (
          <>
            <p className="text-secondary auth-intro">
              請使用管理員帳號登入（需 admin 或 security-officer 權限）。
            </p>
            <form onSubmit={onSubmit} className="auth-form">
              <label className="adm-field">
                <span className="text-secondary adm-field-label">電子郵件</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={error !== null}
                />
              </label>
              <label className="adm-field">
                <span className="text-secondary adm-field-label">密碼</span>
                <PasswordInput
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={error !== null}
                />
              </label>
              {error && (
                <div className="inline-notice inline-notice--warn" role="alert">
                  {error}
                </div>
              )}
              <button type="submit" className="btn-primary" disabled={busy}>
                {busy ? '登入中…' : '登入'}
              </button>
              <button type="button" className="link-button" onClick={showForgot}>
                忘記密碼？
              </button>
            </form>
          </>
        )}
        {view === 'forgot' && (
          <>
            <p className="text-secondary auth-intro">
              輸入平台管理帳號電子郵件，系統會送出密碼重設連結。
            </p>
            <form onSubmit={onForgotSubmit} className="auth-form">
              <label className="adm-field">
                <span className="text-secondary adm-field-label">電子郵件</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  aria-invalid={error !== null}
                />
              </label>
              <div className="adm-field">
                <span className="text-secondary adm-field-label">傳送方式</span>
                <div className="segmented-control">
                  <button type="button" className={resetChannel === 'email' ? 'is-active' : ''} onClick={() => setResetChannel('email')}>
                    電子郵件
                  </button>
                  <button type="button" className={resetChannel === 'sms' ? 'is-active' : ''} onClick={() => setResetChannel('sms')}>
                    簡訊
                  </button>
                </div>
              </div>
              {error && (
                <div className="inline-notice inline-notice--warn" role="alert">
                  {error}
                </div>
              )}
              <button type="submit" className="btn-primary" disabled={busy}>
                {busy ? '寄送中…' : resetChannel === 'sms' ? '寄送重設簡訊' : '寄送重設信'}
              </button>
              <button type="button" className="link-button" onClick={showLogin}>
                返回登入
              </button>
            </form>
          </>
        )}
        {view === 'forgot-sent' && (
          <>
            <p className="text-secondary auth-intro">
              若帳號存在且可使用，密碼重設訊息已送出。請在 15 分鐘內完成設定。
            </p>
            <button type="button" className="btn-primary" onClick={showLogin} style={{ width: '100%' }}>
              返回登入
            </button>
          </>
        )}
        <p className="text-tertiary auth-foot">
          環境：{config.envName} · {config.apiBase.replace(/^https?:\/\//, '')}
        </p>
      </section>
    </div>
  );
}
